import React, { createContext, useContext } from "react";
import styled from "styled-components"
import { colors2 } from "oolib";
import OverlayScrollbar from "@/components/UI/OverlayScrollbar";
import { globalScrollbarStyling } from "oolib/dist/v2/themes/globalStyles";
import { WIDGET_MAX_HEIGHT } from "@/components/analysisModules/reports/ReportLanding/constants";
import { FadeGradientWhenScroll } from "../FadeGradientWhenScroll";

interface VizShellContextValue {
  orientation?: 'horizontal' | 'vertical';
}

const VizShellContext = createContext<VizShellContextValue | undefined>(undefined);

export const useVizShell = () => {
  const context = useContext(VizShellContext);
  if (context === undefined) {
    throw new Error('useVizShell must be used within a VizShell');
  }
  return context;
};

const StyledVizShellWrapper = styled.div`
  display: flex;
  width: 100%;
  height: ${({ fillHeight }) => fillHeight ? '100%' : 'auto'};
  flex: 1;
  overflow-y: auto;
  flex-direction: ${({ orientation}) => orientation === 'vertical' ? 'row' : 'column'};
`

const StyledVizArea = styled.div`
  ${globalScrollbarStyling}
`
export interface VizShellProps {
  children: React.ReactNode;
  orientation?: 'horizontal' | 'vertical';
  colors?: string[];
  style?: React.CSSProperties
  wrapperRef?: React.RefObject<HTMLDivElement>;
  fillHeight?: boolean;
}

export const VizShell = ({ 
  children,
  orientation = 'horizontal',
  style,
  wrapperRef,
  fillHeight = false,
}: VizShellProps) => {
  const contextValue: VizShellContextValue = {
    orientation,
  };

  return(
    <VizShellContext.Provider value={contextValue} >
      <StyledVizShellWrapper ref={wrapperRef} className="viz-shell-wrapper" orientation={orientation} fillHeight={fillHeight} style={style}>
        {children}
      </StyledVizShellWrapper>
    </VizShellContext.Provider>
  )
}

export interface VizAreaProps {
  children: React.ReactNode;
  style?: React.CSSProperties;
  hideGradient?: boolean // some component like heat map and anno widget has their own 
}

export const VizArea = ({ children, style, hideGradient = false }: VizAreaProps) => {
  
  const { orientation } = useVizShell();
  
  return (
    <StyledVizArea
      orientation={orientation}
      className="viz-area"
      style={{ 
        flex: 1, 
        position: 'relative',
        overflow: "auto",
        padding: "1.6rem",
        ...style
    }}>
      {/* @ts-ignore */}
          {children}
          {/* {!hideGradient && <FadeGradientWhenScroll style={{ position: 'sticky', bottom: "-1.6rem", left: 0, width: '100%' }}/>} */}
    </StyledVizArea>
  );
};

export interface VizPanelProps {
  children: React.ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
  style?: React.CSSProperties;
}

export const VizPanel = ({ 
  children, 
  position = 'bottom',
  style
}: VizPanelProps) => {
  const { orientation } = useVizShell();
  
  const getPositionStyles = () => {
    switch (position) {
      case 'top':
        return { order: -1 };
      case 'bottom':
        return { order: 1 };
      default:
        return {};
    }
  };
  if(!children) return null
  return (
    <div
      className={`viz-panel`} 
      style={{
        position: 'sticky',
        ...(orientation === 'vertical' ? { 
              top: 0,
              maxHeight: WIDGET_MAX_HEIGHT + "px",
              overflow: "auto",
              borderLeft: `1px solid ${colors2.grey10}`,
              maxWidth: "30%", // Max 30% width for vertical legend as per rules
            } : 
            { 
              bottom: 0,
              minHeight: "100",
              maxHeight: "25%", // Max 25% height for horizontal legend as per rules
              borderTop: `1px solid ${colors2.grey10}`,
            }),
        ...getPositionStyles(),
        ...style
      }}>
        {/* @ts-ignore */}
        <OverlayScrollbar>
          {children}
        </OverlayScrollbar>
      </div>
  );
};